/**
 * Plan event contract — the bridge between BrowserBus and the
 * task-checklist drawer.
 *
 * Sender: useBrowserBus, when an SSE choreography event arrives with
 * propose_plan / update_plan_step (fired by the user's external MCP
 * client — Claude, ChatGPT, Cursor, …).
 *
 * One receiver: the TaskChecklist React component (or any other panel
 * a host app wires up). It listens for `PLAN_EVENT` on `window`.
 *
 * Why CustomEvent on window instead of context: the SSE hook and the
 * checklist drawer are mounted at different points in the app tree and
 * don't share a context provider above them. A window event is the
 * simplest cross-boundary surface.
 *
 * The contract here is framework-level — no political-CRM specifics.
 */

export interface PlanEntity {
  id?: string;
  name: string;
  type?: string;
}

export interface PlanProposal {
  title: string;
  steps: string[];
  affects?: string;
  reversible?: boolean;
  shape?: string;
  entities?: PlanEntity[];
}

export interface PlanStepUpdate {
  step_index: number;
  status: "pending" | "running" | "success" | "failed" | "skipped";
  result_summary?: string;
}

export const PLAN_EVENT = "cmcp:plan-event";

export interface PlanEventDetail {
  kind: "propose_plan" | "update_plan_step" | "approve_plan" | "clear_plans";
  input?: PlanProposal | PlanStepUpdate;
}

export function dispatchPlanProposed(plan: PlanProposal) {
  if (typeof window === "undefined") return;
  window.dispatchEvent(
    new CustomEvent<PlanEventDetail>(PLAN_EVENT, {
      detail: { kind: "propose_plan", input: plan },
    })
  );
}

export function dispatchPlanStepUpdate(update: PlanStepUpdate) {
  if (typeof window === "undefined") return;
  window.dispatchEvent(
    new CustomEvent<PlanEventDetail>(PLAN_EVENT, {
      detail: { kind: "update_plan_step", input: update },
    })
  );
}

export function dispatchPlanApproved() {
  if (typeof window === "undefined") return;
  window.dispatchEvent(
    new CustomEvent<PlanEventDetail>(PLAN_EVENT, {
      detail: { kind: "approve_plan" },
    })
  );
}
